import React, { useContext, useEffect, useState, useCallback } from "react"
import { RfidActivationLayout } from "layouts/rfid/rfidActivationLayout.tsx"
import {
    Autocomplete,
    TextField,
    Typography,
    Divider,
    FormGroup,
    Box,
} from "@mui/material"
import { OreveButton } from "components/button/OreveButton.tsx"
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1"
import { SelectableChip } from "components/OreveChip.tsx"
import {
    RfidActivationFormContext,
    useActivationFormContext,
} from "layouts/rfid/rfidActivationForm.tsx"
import { useMmsService } from "services/mms/context/MmsServiceContext.tsx"
import { OrevePopup } from "components/popup/OrevePopup.tsx"
import {
    AddDriverPopupMain,
    AddDriverPopupFeedback,
    DuplicateDriverPopup,
    IAddDriverFormProps,
} from "layouts/card-holder/popups/addCardHolderPopup.tsx"
import type { AccountInfo } from "types/list-card-holder"

interface Step2Props {
    goBackToList: () => void
    validateDriver: () => void
}

export const Step2: React.FC<Step2Props> = ({
    goBackToList,
    validateDriver,
}) => {
    const [displayPopup, setDisplayPopup] = useState(false)
    const [vehicleAccount, setVehicleAccount] = useState(true)
    const [chosenAccountType, setChosenAccountType] = useState("vehicle")
    const context = useContext(RfidActivationFormContext)

    useEffect(() => {
        setChosenAccountType(vehicleAccount ? "vehicle" : "driver")
    }, [vehicleAccount])

    return (
        <RfidActivationLayout
            step={2}
            totalSteps={4}
            goBackToList={goBackToList}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "1rem",
                    marginBottom: { xs: "4rem", md: "8rem" },
                    width: "100%",
                }}
            >
                <Typography variant="labelLarge" textAlign="center">
                    À quel compte souhaitez-vous associer cette carte ?
                </Typography>

                <FormGroup>
                    <Box display="flex" gap={2} mb={2} justifyContent="center">
                        <SelectableChip
                            dataCy="rfid__activation--vehicle-account"
                            label="Compte véhicule"
                            selected={vehicleAccount}
                            onClick={() => setVehicleAccount(true)}
                        />
                        <Box
                            sx={{
                                width: "2px",
                                height: "30px",
                                backgroundColor: "#000",
                            }}
                        />
                        <SelectableChip
                            dataCy="rfid__activation--driver-account"
                            label="Compte conducteur"
                            selected={!vehicleAccount}
                            onClick={() => setVehicleAccount(false)}
                        />
                    </Box>
                </FormGroup>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "1.5rem",
                        width: "100%",
                    }}
                >
                    <Autocomplete
                        // open={true}
                        // disablePortal
                        data-cy="rfid__activation--cardAssociation"
                        sx={{ width: { xs: "100%", sm: "350px", md: "400px" } }}
                        options={
                            context?.activeAccountList?.filter(
                                (a) => a.type === chosenAccountType
                            ) ?? []
                        }
                        isOptionEqualToValue={(option, value) =>
                            option.info.id === value.info.id
                        }
                        value={context?.chosenAccount}
                        onChange={(_evt, newValue) =>
                            context?.setChosenAccount(newValue ?? null)
                        }
                        getOptionLabel={(option) =>
                            option.type === "vehicle"
                                ? option.info.licensePlate
                                : `${option.info.name} (${option.info.email})`
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={
                                    vehicleAccount
                                        ? "Immatriculation"
                                        : "Nom du conducteur"
                                }
                            />
                        )}
                        renderOption={(props, option, { index }) => {
                            const { key, ...optionProps } = props
                            return (
                                <li
                                    key={key}
                                    {...optionProps}
                                    data-cy={`rfid__activation--cardAssociationItem-${index + 1}`}
                                >
                                    {
                                        option.type === "vehicle"
                                            ? option.info.licensePlate
                                            : `${option.info.name} (${option.info.email})`
                                    }
                                </li>
                            )
                        }}
                    />

                    {context?.chosenAccount && (
                        <OreveButton
                            sx={{
                                maxHeight: "40px",
                                width: { xs: "100%", sm: "auto" },
                            }}
                            value="Valider"
                            data-cy="rfid__activation--validate-driver"
                            onClick={validateDriver}
                        />
                    )}
                </Box>

                <Divider sx={{ width: "100%", my: 2 }}>ou</Divider>

                <OreveButton
                    data-cy="rfid__activation--add-driver"
                    icon={PersonAddAlt1Icon}
                    value="Ajouter un conducteur"
                    variant="outlined"
                    onClick={() => setDisplayPopup(true)}
                />
            </Box>

            <AddDriverPopup
                open={displayPopup}
                handleClose={() => setDisplayPopup(false)}
            />
        </RfidActivationLayout>
    )
}

const AddDriverPopup = ({
    open,
    handleClose,
}: {
    open: boolean
    handleClose: () => void
}) => {
    const { cardHolderService } = useMmsService()
    const { useAddCardHolder } = cardHolderService
    const {
        data,
        success,
        isLoading,
        sendRequest: addDriver,
    } = useAddCardHolder()
    const [displayResponse, setDisplayResponse] = useState(false)
    const [displayWarningDriver, setDisplayWarningDriver] = useState(false)
    const [driverInfo, setDriverInfo] = useState<IAddDriverFormProps>({
        firstName: "",
        lastName: "",
        email: "",
    })
    const { setChosenAccount, fetchDrivers, activeAccountList } =
        useActivationFormContext()

    useEffect(() => {
        if (data) {
            const refetch = async (account: AccountInfo) => {
                await fetchDrivers()
                setChosenAccount(account)
            }
            refetch(data as AccountInfo)
        }
    }, [data])

    const getFormData = useCallback((postDriver: IAddDriverFormProps) => {
        setDriverInfo(postDriver)
    }, [])

    const onAddDriver = useCallback(
        async (postDriver: IAddDriverFormProps, existed: boolean) => {
            if (existed) {
                getFormData(postDriver)
                setDisplayWarningDriver(true)
            } else {
                await addDriver(postDriver)
                setDisplayResponse(true)
            }
        },
        [addDriver]
    )

    useEffect(() => {
        if (open) setDisplayResponse(false)
        else fetchDrivers()
    }, [open])

    return (
        <OrevePopup open={open} handleClose={handleClose}>
            {displayWarningDriver ? (
                <DuplicateDriverPopup
                    setDisplayResponse={setDisplayResponse}
                    setDisplayWarningDriver={setDisplayWarningDriver}
                    onAddDriver={onAddDriver}
                    driverInfo={driverInfo}
                />
            ) : displayResponse ? (
                <AddDriverPopupFeedback
                    success={success ?? false}
                    handleClose={handleClose}
                    setDisplayResponse={setDisplayResponse}
                />
            ) : (
                <AddDriverPopupMain
                    onAddDriver={onAddDriver}
                    isLoading={isLoading}
                    drivers={activeAccountList?.map((item) => item.info) ?? []}
                    currentDriver={driverInfo}
                />
            )}
        </OrevePopup>
    )
}
